



































































































































































/* eslint-disable class-methods-use-this */
import { Vue, Component, Prop } from 'vue-property-decorator';
import { inject } from 'inversify-props';
import { InjectionIdEnum } from '@/enums/injection-id.enum';
import CRMEvoTalksService from '@/services/crm/evotalks.service';
import EvoTalksConversaResumoModel from '@/models/crm/evotalks-conversa-resumo.model';
import EvoTalksMensagemModel from '@/models/crm/evotalks-mensagem.model';
import { IKeyValue } from '@/interfaces/key-value.interface';
import dayjs from '@/plugins/dayjs';

@Component
export default class CrmEvoTalksChatHistoryViewer extends Vue {
  @inject(InjectionIdEnum.CRMEvoTalksService)
  private evoTalksService!: CRMEvoTalksService;

  @Prop({ required: true })
  conversa!: EvoTalksConversaResumoModel;

  currentConversa: EvoTalksConversaResumoModel = {} as EvoTalksConversaResumoModel;

  allMessages: EvoTalksMensagemModel[] = [];

  messageListLimit = 30;

  private readonly messageListItemPerPage = 30;

  contatoChats: EvoTalksConversaResumoModel[] = [];

  private olderChats: EvoTalksConversaResumoModel[] = [];

  private newerChats: EvoTalksConversaResumoModel[] = [];

  private olderChatIndex = 0;

  private newerChatIndex = 0;

  loadingOlder = false;

  loadingNewer = false;

  async mounted(): Promise<void> {
    this.currentConversa = this.conversa;
    await this.loadMessages();
    await this.loadContatoChats();
  }

  async loadMessages(chatId?: string): Promise<void> {
    const id = chatId || this.currentConversa?.evoChatId;
    if (!id) {
      this.allMessages = [];
      return;
    }
    const loader = this.setBusyLoader();
    try {
      const messages = await this.evoTalksService.getChatMessagesFromApi(id);
      const protocolMsg = this.buildProtocolMessage(this.currentConversa);
      this.allMessages = protocolMsg ? [protocolMsg, ...messages] : messages;
      this.messageListLimit = this.messageListItemPerPage;
    } catch (err) {
      this.$notify.error(err && (err as Error).message);
      this.allMessages = [];
    } finally {
      loader.hide();
    }
  }

  private buildProtocolMessage(chat: EvoTalksConversaResumoModel): EvoTalksMensagemModel | null {
    if (!chat || !chat.evoChatId) return null;
    return {
      messageId: `protocol_${chat.evoChatId}`,
      chatId: chat.evoChatId,
      content: `Atendimento iniciado - Protocolo: ${chat.protocolo || chat.evoChatId}`,
      contentType: 'TEXT',
      mediaUrl: '',
      direction: '',
      senderType: '',
      senderId: '',
      timestamp: chat.dataInicio,
      eventType: 'CHAT_OPENED',
      isSystemLog: true,
    } as EvoTalksMensagemModel;
  }

  onShowMoreItems(): void {
    this.messageListLimit += this.messageListItemPerPage;
  }

  get visibleMessages(): EvoTalksMensagemModel[] {
    return this.allMessages.slice(0, this.messageListLimit);
  }

  get showMoreItemsBtn(): boolean {
    return this.allMessages.length > this.messageListLimit;
  }

  get hasOlderChats(): boolean {
    return this.olderChatIndex < this.olderChats.length;
  }

  get hasNewerChats(): boolean {
    return this.newerChatIndex < this.newerChats.length;
  }

  get isConversaClosed(): boolean {
    const s = this.currentConversa?.status;
    return !!this.currentConversa?.dataFechamento || s === 'FECHADA' || s === 'CLOSED' || s === 'Fechada';
  }

  get chatBoundary(): IKeyValue<string> {
    const data: IKeyValue<string> = {};
    let previousChatId: string | null = null;
    this.visibleMessages.forEach((item, index) => {
      if (item.chatId && item.chatId !== previousChatId && previousChatId !== null) {
        const chat = this.contatoChats.find((c) => c.evoChatId === item.chatId);
        if (chat) {
          data[index] = `Conversa ${chat.protocolo || chat.evoChatId} — ${this.formatDate(chat.dataInicio)}`;
        } else if (item.chatId === this.currentConversa?.evoChatId) {
          const protocolo = this.currentConversa.protocolo || item.chatId;
          data[index] = `Conversa ${protocolo} — ${this.formatDate(this.currentConversa.dataInicio)}`;
        } else {
          data[index] = `Conversa ${item.chatId}`;
        }
      }
      if (item.chatId) previousChatId = item.chatId;
    });
    return data;
  }

  async loadContatoChats(): Promise<void> {
    if (!this.currentConversa?.evoChatId) return;
    try {
      this.contatoChats = await this.evoTalksService.getContatoChats(this.currentConversa.evoChatId);
      const currentStart = this.currentConversa.dataInicio
        ? new Date(this.currentConversa.dataInicio).getTime()
        : 0;
      this.newerChats = this.contatoChats
        .filter((c) => c.dataInicio && new Date(c.dataInicio).getTime() > currentStart)
        .reverse();
      this.olderChats = this.contatoChats
        .filter((c) => c.dataInicio && new Date(c.dataInicio).getTime() < currentStart);
    } catch (err) {
      this.contatoChats = [];
      this.olderChats = [];
      this.newerChats = [];
    }
  }

  async onLoadOlderMessages(): Promise<void> {
    if (!this.hasOlderChats || this.loadingOlder) return;
    this.loadingOlder = true;
    try {
      const olderChat = this.olderChats[this.olderChatIndex];
      const olderMessages = await this.evoTalksService.getChatMessagesFromApi(olderChat.evoChatId);
      const protocolMsg = this.buildProtocolMessage(olderChat);
      const prepend = protocolMsg ? [protocolMsg, ...olderMessages] : olderMessages;
      this.allMessages = [...prepend, ...this.allMessages];
      this.messageListLimit += prepend.length;
      this.olderChatIndex += 1;
    } catch (err) {
      this.$notify.error(err && (err as Error).message);
    } finally {
      this.loadingOlder = false;
    }
  }

  async onLoadNewerMessages(): Promise<void> {
    if (!this.hasNewerChats || this.loadingNewer) return;
    this.loadingNewer = true;
    try {
      const newerChat = this.newerChats[this.newerChatIndex];
      const newerMessages = await this.evoTalksService.getChatMessagesFromApi(newerChat.evoChatId);
      const protocolMsg = this.buildProtocolMessage(newerChat);
      const append = protocolMsg ? [protocolMsg, ...newerMessages] : newerMessages;
      this.allMessages = [...this.allMessages, ...append];
      this.messageListLimit = this.allMessages.length;
      this.newerChatIndex += 1;
    } catch (err) {
      this.$notify.error(err && (err as Error).message);
    } finally {
      this.loadingNewer = false;
    }
  }

  get dateSeparator(): IKeyValue<string> {
    const data: IKeyValue<string> = {};
    let previousDate: dayjs.Dayjs | null = null;
    this.visibleMessages.forEach((item, index) => {
      if (!item.timestamp) return;
      const currentDate = dayjs(item.timestamp);
      if (index === 0 || (previousDate && !previousDate.isSame(currentDate, 'date'))) {
        data[index] = currentDate.format('DD/MM/YYYY');
      }
      previousDate = currentDate;
    });
    return data;
  }

  hasFile(msg: EvoTalksMensagemModel): boolean {
    return !!msg.fileId || !!msg.mediaUrl;
  }

  mediaSrc(msg: EvoTalksMensagemModel): string {
    const chatId = msg.chatId || this.currentConversa?.evoChatId;
    if (msg.fileId && chatId) {
      return this.evoTalksService.getFileDownloadUrl(chatId, msg.fileId);
    }
    return msg.mediaUrl || '';
  }

  isImage(msg: EvoTalksMensagemModel): boolean {
    if (!this.hasFile(msg)) return false;
    if (msg.fileMimeType && msg.fileMimeType.toLowerCase().startsWith('image/')) return true;
    return msg.contentType === 'IMAGE';
  }

  isAudio(msg: EvoTalksMensagemModel): boolean {
    if (!this.hasFile(msg)) return false;
    if (msg.fileMimeType && msg.fileMimeType.toLowerCase().startsWith('audio/')) return true;
    return msg.contentType === 'AUDIO';
  }

  isVideo(msg: EvoTalksMensagemModel): boolean {
    if (!this.hasFile(msg)) return false;
    if (msg.fileMimeType && msg.fileMimeType.toLowerCase().startsWith('video/')) return true;
    return msg.contentType === 'VIDEO';
  }

  formatDate(date: Date | string | null): string {
    if (!date) return '';
    return dayjs(date).format('DD/MM/YYYY HH:mm:ss');
  }

  formatTime(date: Date | string | null): string {
    if (!date) return '';
    return dayjs(date).format('HH:mm');
  }

  formatPhone(phone: string | null): string {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.startsWith('55') && digits.length >= 12) {
      const ddd = digits.substring(2, 4);
      const num = digits.substring(4);
      if (num.length === 9) return `+55 (${ddd}) ${num.substring(0, 5)}-${num.substring(5)}`;
      if (num.length === 8) return `+55 (${ddd}) ${num.substring(0, 4)}-${num.substring(4)}`;
      return `+55 (${ddd}) ${num}`;
    }
    if (digits.length === 11) return `(${digits.substring(0, 2)}) ${digits.substring(2, 7)}-${digits.substring(7)}`;
    if (digits.length === 10) return `(${digits.substring(0, 2)}) ${digits.substring(2, 6)}-${digits.substring(6)}`;
    return phone;
  }

  messageStatusIcon(msg: EvoTalksMensagemModel): string {
    if (msg.clientReadTime) return 'mdi-check-all';
    if (msg.clientReceivedTime) return 'mdi-check-all';
    return 'mdi-check';
  }

  messageStatusColor(msg: EvoTalksMensagemModel): string {
    if (msg.clientReadTime) return 'primary';
    return '';
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
  }

  private setBusyLoader() {
    return this.$loading.show({
      container: this.$refs.viewerContainer,
      canCancel: false,
    });
  }
}
